// BarraTecnicos — faixa horizontal com cada técnico ativo hoje.
// Avatar com halo colorido + status + meta info (próxima OS, etc).

import { useState } from 'react';
import { osLabel } from '../lib/visita.js';

const STATUS_INFO = {
  em_rota:       { label: '🚗 em rota',     halo: '#22c55e' },
  atendendo:     { label: '🔧 atendendo',   halo: '#3b82f6' },
  pausa:         { label: '☕ pausa',       halo: '#fbbf24' },
  ocioso:        { label: '⏸ ocioso',      halo: '#94a3b8' },
  parado_alerta: { label: '⚠ parado',      halo: '#ef4444' },
  sem_gps:       { label: '○ sem GPS',     halo: '#cbd5e1' },
};

// Cores espelham COR_POR_SETOR do backend (routes/dashboard.js)
const SETOR_INFO = {
  suporte:        { label: 'Suporte',        cor: '#1e6fc8' },
  ativacao:       { label: 'Ativação',       cor: '#16a34a' },
  recolhimento:   { label: 'Recolhimento',   cor: '#ea580c' },
  infraestrutura: { label: 'Infraestrutura', cor: '#9333ea' },
  operacional:    { label: 'Operacional',    cor: '#6b7280' },
};

function formatHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

function minutosAte(iso) {
  if (!iso) return null;
  const diff = (new Date(iso) - new Date()) / 60000;
  return Math.round(diff);
}

export default function BarraTecnicos({ tecnicos = [], onClickTecnico, tecnicoSelecionado }) {
  const [filtro, setFiltro] = useState('todos');           // status
  const [filtroSetor, setFiltroSetor] = useState('todos'); // setor

  const filtrados = tecnicos.filter(t => {
    const matchStatus = filtro === 'todos' || t.status_calc === filtro;
    const matchSetor  = filtroSetor === 'todos' || (t.setor_primario || 'sem_dados') === filtroSetor;
    return matchStatus && matchSetor;
  });

  const contagens = tecnicos.reduce((acc, t) => {
    acc[t.status_calc] = (acc[t.status_calc] || 0) + 1;
    return acc;
  }, {});

  // Contagens por setor (só dos que estão na lista atual)
  const contagensSetor = tecnicos.reduce((acc, t) => {
    const s = t.setor_primario || 'sem_dados';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="tecnicos-bar">
      <div className="tecnicos-head">
        <h2>Técnicos em campo · {tecnicos.length} ativos hoje</h2>

        {/* Legenda de setores (clicável → filtra) */}
        <div className="tecnicos-legend">
          <span className={filtroSetor==='todos' ? 'lg-pill active' : 'lg-pill'}
                onClick={() => setFiltroSetor('todos')}
                title="Mostrar todos os setores">
            <span className="lg-dot" style={{ background: '#94a3b8' }}></span>
            Todos
          </span>
          {Object.entries(SETOR_INFO).map(([key, info]) => {
            const n = contagensSetor[key] || 0;
            if (n === 0) return null;  // não mostra setor sem técnicos
            return (
              <span key={key}
                    className={filtroSetor===key ? 'lg-pill active' : 'lg-pill'}
                    onClick={() => setFiltroSetor(filtroSetor === key ? 'todos' : key)}
                    title={`Filtrar por ${info.label.toLowerCase()} (${n})`}>
                <span className="lg-dot" style={{ background: info.cor }}></span>
                {info.label} ({n})
              </span>
            );
          })}
        </div>

        <div className="tecnicos-filter">
          <span className={filtro==='todos'?'active':''} onClick={() => setFiltro('todos')}>
            Todos
          </span>
          {Object.entries(STATUS_INFO).map(([key, info]) => {
            const c = contagens[key] || 0;
            if (c === 0 && key !== 'parado_alerta') return null;
            return (
              <span key={key} className={filtro===key?'active':''} onClick={() => setFiltro(key)}>
                {info.label.split(' ').slice(1).join(' ')} ({c})
              </span>
            );
          })}
        </div>
      </div>

      <div className="tecnicos-grid">
        {filtrados.length === 0 && (
          <p style={{ color: '#9ca3af', fontSize: 13, padding: '8px 4px' }}>
            Nenhum técnico {filtroSetor !== 'todos' ? `de ${SETOR_INFO[filtroSetor]?.label.toLowerCase() || filtroSetor}` : ''} {filtro==='todos'?(filtroSetor==='todos'?'ativo':''):`com status ${filtro}`}.
          </p>
        )}
        {filtrados.map(t => {
          const info = STATUS_INFO[t.status_calc] || STATUS_INFO.ocioso;
          const isSelected = tecnicoSelecionado === t.id;
          const isAlert = t.status_calc === 'parado_alerta';
          const minProx = t.proxima_visita ? minutosAte(t.proxima_visita.janela_inicio) : null;

          let metaTexto = '';
          let metaClass = '';
          if (t.visita_em_atendimento) {
            metaTexto = `desde ${formatHora(t.visita_em_atendimento.janela_inicio)} · OS ${osLabel(t.visita_em_atendimento)}`;
          } else if (t.proxima_visita) {
            if (minProx >= 0) {
              metaTexto = `próx ${formatHora(t.proxima_visita.janela_inicio)} (em ${minProx}min)`;
              metaClass = minProx <= 15 ? 'ok' : '';
            } else {
              metaTexto = `próx atrasada ${-minProx}min`;
              metaClass = 'late';
            }
          } else if (t.visitas_total > 0 && t.visitas_feitas === t.visitas_total) {
            metaTexto = `${t.visitas_total} OS concluídas hoje`;
          } else {
            metaTexto = 'sem rota planejada';
          }

          return (
            <div
              key={t.id}
              className="tec-card"
              onClick={() => onClickTecnico && onClickTecnico(t)}
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? '#1f2937' : isAlert ? '#fecaca' : '#f3f4f6',
                background: isAlert ? '#fef2f2' : (isSelected ? '#f9fafb' : 'white'),
              }}
            >
              <div className="tec-card-head">
                <div className="tec-avatar-big" style={{ background: t.avatar_cor || '#fb923c' }}>
                  {t.iniciais}
                  <div className="status-ring" style={{ borderColor: info.halo }}></div>
                </div>
                <div>
                  <div className="tec-name">{t.apelido || t.nome.split(' ')[0]}</div>
                  <div className="tec-status-text">{info.label}</div>
                </div>
              </div>
              <div className={`tec-meta-row ${metaClass}`}>{metaTexto}</div>
              {t.visitas_total > 0 && (
                <div className="tec-progress">
                  <div style={{ width: `${(t.visitas_feitas / t.visitas_total) * 100}%` }}></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
